import { API_BASE_URL } from '../config/runtime';

/**
 * Generate image URL for animal photos
 * Uses backend API endpoint for database-stored images with cache busting
 */
export const getImageUrl = (animalId: string | null | undefined): string => {
  if (!animalId) {
    return '/images/default-animal.svg'; // Default image served from frontend
  }

  // Use the new database image API endpoint with cache busting
  const timestamp = Date.now();
  return `${API_BASE_URL}/animals/${animalId}/image?t=${timestamp}`;
};

/**
 * Get authentication headers for API requests
 * Includes JWT token from localStorage if available
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('shelter-token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Handle API responses with error checking
 * Parses JSON and throws errors for failed requests
 */
const handleResponse = async (response: Response) => {
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'API request failed');
  }

  return data;
};

/**
 * Authentication API endpoints
 * Handles login, profile management, and password changes
 */
export const authAPI = {
  login: async (credentials: { email: string; password: string }) => {
    const response = await fetch(`${API_BASE_URL}/session/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    return handleResponse(response);
  },

  getProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/session/profile`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateProfile: async (profileData: any) => {
    const response = await fetch(`${API_BASE_URL}/session/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(profileData),
    });
    return handleResponse(response);
  },

  changePassword: async (passwordData: { currentPassword: string; newPassword: string }) => {
    const response = await fetch(`${API_BASE_URL}/session/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(passwordData),
    });
    return handleResponse(response);
  },

  // User management endpoints (Admin only)
  getUsers: async (params?: { page?: number; limit?: number }) => {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const response = await fetch(`${API_BASE_URL}/session/users?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createUser: async (userData: { email: string; firstName: string; lastName: string; role: string; temporaryPassword: string }) => {
    const response = await fetch(`${API_BASE_URL}/session/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  updateUserStatus: async (userId: string, isActive: boolean) => {
    const response = await fetch(`${API_BASE_URL}/session/users/${userId}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });
    return handleResponse(response);
  },

  updateUser: async (userId: string, userData: { firstName: string; lastName: string; email: string; role: string; temporaryPassword?: string }) => {
    const response = await fetch(`${API_BASE_URL}/session/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  deleteUser: async (userId: string) => {
    const response = await fetch(`${API_BASE_URL}/session/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  }
};

// Animals API
export const animalsAPI = {
  getAnimals: async (params?: any) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/animals?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAnimalsStats: async () => {
    const response = await fetch(`${API_BASE_URL}/animals/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAnimal: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/animals/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createAnimal: async (animalData: any) => {
    const formData = new FormData();

    // Add all animal data to form data
    Object.keys(animalData).forEach(key => {
      if (key === 'imageFile' && animalData[key]) {
        // Add the image file
        formData.append('image', animalData[key]);
      } else if (key !== 'imageFile') {
        // Add other data as JSON string
        formData.append(key, typeof animalData[key] === 'object' ? JSON.stringify(animalData[key]) : animalData[key]);
      }
    });

    const token = localStorage.getItem('shelter-token');
    const response = await fetch(`${API_BASE_URL}/animals`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
        // Don't set Content-Type for FormData, let the browser set it with boundary
      },
      body: formData,
    });
    return handleResponse(response);
  },

  updateAnimal: async (id: string, animalData: any) => {
    const formData = new FormData();

    // Add all animal data to form data
    Object.keys(animalData).forEach(key => {
      if (key === 'imageFile' && animalData[key]) {
        // Add the image file
        formData.append('image', animalData[key]);
      } else if (key !== 'imageFile') {
        // Add other data as JSON string
        formData.append(key, typeof animalData[key] === 'object' ? JSON.stringify(animalData[key]) : animalData[key]);
      }
    });

    const token = localStorage.getItem('shelter-token');
    const response = await fetch(`${API_BASE_URL}/animals/${id}`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
        // Don't set Content-Type for FormData, let the browser set it with boundary
      },
      body: formData,
    });
    return handleResponse(response);
  },

  deleteAnimal: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/animals/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  addNote: async (id: string, noteData: any) => {
    const response = await fetch(`${API_BASE_URL}/animals/${id}/notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(noteData),
    });
    return handleResponse(response);
  },

  addMedicalRecord: async (id: string, recordData: any) => {
    const response = await fetch(`${API_BASE_URL}/animals/${id}/medical`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(recordData),
    });
    return handleResponse(response);
  }
};

// Volunteers API
export const volunteersAPI = {
  getVolunteers: async (params?: any) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/volunteers?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getVolunteer: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/volunteers/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createVolunteer: async (volunteerData: any) => {
    const response = await fetch(`${API_BASE_URL}/volunteers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(volunteerData),
    });
    return handleResponse(response);
  },

  updateVolunteer: async (id: string, volunteerData: any) => {
    const response = await fetch(`${API_BASE_URL}/volunteers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(volunteerData),
    });
    return handleResponse(response);
  },

  deleteVolunteer: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/volunteers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateStatus: async (id: string, status: string) => {
    const response = await fetch(`${API_BASE_URL}/volunteers/${id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse(response);
  },

  async addVolunteerHours(volunteerId: string, hoursData: { hours: number; activity?: string; description?: string; date?: string }) {
    const response = await fetch(`${API_BASE_URL}/volunteers/${volunteerId}/hours`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(hoursData),
    });
    return handleResponse(response);
  }
};

// Adoptions API
export const adoptionsAPI = {
  getAdoptions: async (params?: any) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/adoptions/applications?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAdoption: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/adoptions/applications/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  createAdoption: async (adoptionData: any) => {
    const response = await fetch(`${API_BASE_URL}/adoptions/applications`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(adoptionData),
    });
    return handleResponse(response);
  },

  updateAdoption: async (id: string, adoptionData: any) => {
    const response = await fetch(`${API_BASE_URL}/adoptions/applications/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(adoptionData),
    });
    return handleResponse(response);
  },

  updateStatus: async (id: string, status: string) => {
    const response = await fetch(`${API_BASE_URL}/adoptions/applications/${id}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse(response);
  },

  deleteAdoption: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/adoptions/applications/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAdoptionsStats: async () => {
    const response = await fetch(`${API_BASE_URL}/adoptions/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  }
};

// Settings API
export const settingsAPI = {
  getSettings: async (category?: string) => {
    const queryString = category ? `?category=${category}` : '';
    const response = await fetch(`${API_BASE_URL}/settings${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateSettings: async (settings: any) => {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ settings }),
    });
    return handleResponse(response);
  },

  resetSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/settings/reset`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  }
};

// Profile API
export const profileAPI = {
  getProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/profile`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateProfile: async (profileData: any) => {
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(profileData),
    });
    return handleResponse(response);
  },

  updateNotificationPreferences: async (preferences: any) => {
    const response = await fetch(`${API_BASE_URL}/profile/notifications`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notificationPreferences: preferences }),
    });
    return handleResponse(response);
  }
};

// Reports API
export const reportsAPI = {
  generateReport: async (reportData: { reportType: string; reportName: string; reportPeriod?: string }) => {
    const response = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reportData),
    });
    return handleResponse(response);
  },

  getReports: async (params?: any) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/reports?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getReport: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  }
};

// Contact API (for admin)
export const contactAPI = {
  getMessages: async (params?: any) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/contact?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getMessage: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  replyToMessage: async (id: string, reply: string) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}/reply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reply }),
    });
    return handleResponse(response);
  },

  deleteMessage: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getStats: async () => {
    const response = await fetch(`${API_BASE_URL}/contact/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getAttachmentUrl: (id: string) => {
    const token = localStorage.getItem('shelter-token');
    return `${API_BASE_URL}/contact/${id}/attachment?token=${token}`;
  }
};
